<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import {
    ref,
    computed,
    watch,
    onMounted,
    onUnmounted,
    onBeforeMount,
} from "vue"
import { useRoute, useRouter } from "vue-router"
import { watchDebounced } from "@vueuse/core"
import { inject } from "vue"
import { useToast } from "primevue/usetoast"
import { useI18n } from "vue-i18n"
import type { Services } from "@services"
import type { DroitAccesEtat, Source, SourceListItem, SourceType } from "@types"
import { mapFiltersToApiParams, formatDateFr } from "@core/helpers"
import {
    IOT_TYPES_LIST,
    METER_TYPES_LIST,
    SOURCES_TYPES,
} from "@core/constants"
import { useFetchWrapper, useResponsive, useRedirect } from "@core/composables"
import { storeToRefs } from "pinia"
import { useAuthStore } from "@core/stores/auth.store"
import { useConfigStore } from "@core/stores/config.store"
import type { Address } from "@types"
import { receptionWifiConfig } from "./source-list-reception.helper"
import { texteDeuxNombres } from "@core/composables/decompte-nombres.helper"
import { rafraichirPlafondServi } from "@core/composables/plafond-rafraichissement"

// ================================
// PROPS & EMITS
// ================================
// Aucune props ni emits

// ================================
// COMPOSABLES
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Objets connectés",
            filters: "Filtres",
            filtersWithCount: "Filtres ({count})",
            export: "Exporter mes objets connectés",
            resultsFound: "Résultats trouvés :",
            total: "Total",
            emptyState: "Aucune source trouvée",
            perdus: "Perdus",
            finDeVie: "Fin de vie",
            capteursDeplaces: "Capteurs déplacés",
            errorSummary: "Erreur",
            exportSuccessSummary: "Export réussi",
            exportSuccessDetail: "Le fichier est en cours de téléchargement",
            exportNoFileSummary: "Export",
            exportNoFileDetail: "Aucun fichier à télécharger",
            exportErrorDetail: "Une erreur est survenue lors de l'export",
            filterReset: "Réinitialiser",
            filterCategorySourceTypes: "Types de source",
            filterCategorySensorStates: "États des capteurs",
            filterCategoryBattery: "Niveau de batterie des capteurs",
            filterCategorySensorLocation: "Localisation des capteurs",
            filterElec: "Compteurs électriques",
            filterGaz: "Compteurs de gaz",
            filterRcu: "Réseau de chaleur urbain",
            filterCapteur: "Capteurs",
            filterEau: "Compteurs d'eau",
            filterOk: "Fonctionnels",
            filterInLoss: "En perte",
            filterKo: "Perdus",
            filterNeverEmit: "Aucun message reçu",
            filterPceKo: "Dysfonctionnement PCE",
            filterBattery100: "100%",
            filterBattery75: "75%",
            filterBattery50: "50%",
            filterBattery25: "25%",
            filterBattery0: "0%",
            filterBatteryEOL: "Fin de vie",
            filterFixed: "Capteurs fixes",
            filterMoved: "Capteurs déplacés",
            exportErrorMessage: "Erreur lors de l'export",
            loadKpisErrorMessage: "Impossible de charger les indicateurs",
            loadSourcesErrorMessage: "Impossible de charger les sources",
            retry: "Réessayer",
            loadingSources: "Chargement des sources...",
            breadcrumbAccueil: "Accueil",
            search: "Rechercher",
            filterCategoryConsent: "Consentement",
            filterConsentRequestPending:
                "Uniquement les objets liés à une demande de consentement en cours",
            sourceCountBreakdownTitle: "Répartition des objets connectés",
            sourceCountBreakdownLine1: "Nombre total d’objets connectés : {n}.",
            sourceCountBreakdownLine2:
                "Dont avec demande de consentement en attente de validation : {n}.",
            consentBuildingListUnnamed: "Sans nom",
            // État de RÉCEPTION d'un compteur Enedis (DEV-1581, A11) : le verdict du
            // service enedis via dapi `data_retrieval.update.state`, jamais la
            // présence d'un champ. Inconnu = aucun signal, seulement la date nue.
            receptionAJour:
                "Réception à jour : chaque donnée attendue est arrivée dans son délai",
            receptionEnAttente:
                "Réception en attente : une donnée attendue manque ou est en retard",
            receptionEnCours: "Une mise à jour des données est en cours",
            receptionBloquee:
                "Réception bloquée : la collecte est arrêtée (autorisation ou panne)",
            receptionInconnue: "État de réception non disponible",
            dda: {
                a_valider:
                    "La demande de droit d'accès est en attente de validation",
                active: "Le droit d'accès est actif",
                error: "Déclaration GRDF invalide",
                error_tooltip:
                    "GRDF a besoin du consentement pour partager les données",
            },
        },
    },
})
const services = inject<Services>("services")
const route = useRoute()
const router = useRouter()
const { redirectWrapper } = useRedirect()
const toast = useToast()
const authStore = useAuthStore()
const configStore = useConfigStore()
const { isMobile } = useResponsive()
const {
    isCompanyAdmin,
    isClientHomeys,
    isSubscriptionActive,
    companyBetaFeatures,
    companyId,
    companyPlafond,
} = storeToRefs(authStore)

// ================================
// STATE
// ================================

const PAGE_SIZE = 50
/**
 * Liste des sources chargées (accumulée au fil des pages)
 */
const sources = ref<SourceListItem[]>([])

/**
 * État de pagination
 */
const pagination = ref({
    page: 1,
    limit: PAGE_SIZE,
    has_more: true,
})

/**
 * Filtres sélectionnés (tableau de strings : elec, gaz, rcu, etc.)
 */
const filters = ref<string[]>([])

/**
 * Nombre total de sources
 */
const total = ref(0)

/**
 * Nombre total de sources après filtrage
 */
const totalFilter = ref<number | null>(null)

/**
 * Nombre de sources avec demande de consentement en cours (tooltip UC1, GET count)
 */
const totalConsentPending = ref(0)

/**
 * Indicateurs clés (KPIs)
 */
const kpis = ref<{ type: string; value: number }[]>([])

/**
 * Source sélectionnée pour suppression
 */
const sourceToDelete = ref<Source | null>(null)

/**
 * Boîte de suppression ouverte (SourceDeleteDialog : texte, sort de la place
 * servi, appel de suppression et notifications)
 */
const deleteDialogVisible = ref(false)

/**
 * Dialog : choix du bâtiment (clic carte ou bouton, plusieurs bâtiments)
 */
const consentBuildingListDialogVisible = ref(false)
const consentBuildingListForDialog = ref<
    { _id: string; name: string; address: Address; niveau: string }[]
>([])

/**
 * Référence au popover de filtres
 */
const filterPopoverRef = ref<{
    show: (e: Event) => void
    hide: () => void
} | null>(null)

/**
 * Filtres en cours de sélection (avant clic sur "Rechercher")
 */
const pendingFilters = ref<string[]>([])

/**
 * Après un clic sur « Rechercher », la recherche est lancée tout de suite ; on ignore
 * une fois le prochain passage du watchDebounced sur `getQueryParams` pour éviter un double fetch.
 */
const skipNextDebouncedQueryParamsFetch = ref(false)

/**
 * Bêta-feature entreprise : affiche le filtre « Compteurs d'eau » (valeur API `eau`).
 */
const getHasSigduBeta = computed(
    () => companyBetaFeatures.value?.sigdu === true
)

/**
 * Filtres disponibles organisés par catégorie (libellés i18n)
 */
const getFilterCategories = computed(() => {
    const sourceTypeFilters: Array<{ label: string; value: string }> = [
        { label: t("filterElec"), value: "elec" },
        { label: t("filterGaz"), value: "gaz" },
    ]
    if (getHasSigduBeta.value) {
        sourceTypeFilters.push({ label: t("filterEau"), value: "eau" })
    }
    sourceTypeFilters.push(
        { label: t("filterRcu"), value: "rcu" },
        { label: t("filterCapteur"), value: "capteur" }
    )
    return [
        {
            name: t("filterCategorySourceTypes"),
            filters: sourceTypeFilters,
        },
        {
            name: t("filterCategoryConsent"),
            filters: [
                {
                    label: t("filterConsentRequestPending"),
                    value: "consentRequestPending",
                },
                { label: t("filterPceKo"), value: "pceKo" },
            ],
        },
        {
            name: t("filterCategorySensorStates"),
            filters: [
                { label: t("filterOk"), value: "ok" },
                { label: t("filterInLoss"), value: "inLoss" },
                { label: t("filterKo"), value: "ko" },
                { label: t("filterNeverEmit"), value: "neverEmit" },
            ],
        },
        {
            name: t("filterCategoryBattery"),
            filters: [
                { label: t("filterBattery100"), value: "battery100" },
                { label: t("filterBattery75"), value: "battery75" },
                { label: t("filterBattery50"), value: "battery50" },
                { label: t("filterBattery25"), value: "battery25" },
                { label: t("filterBattery0"), value: "battery0" },
                { label: t("filterBatteryEOL"), value: "batteryEOL" },
            ],
        },
        {
            name: t("filterCategorySensorLocation"),
            filters: [
                { label: t("filterFixed"), value: "fixed" },
                { label: t("filterMoved"), value: "moved" },
            ],
        },
    ]
})

/**
 * Fil d'Ariane pour le header (Accueil → Liste des sources)
 */
const getBreadcrumb = computed(() => [
    {
        icon: "pi pi-home",
        label: t("breadcrumbAccueil"),
        route: "/",
    },
    { icon: "pi pi-sitemap", label: t("title") },
])

// ================================
// FETCH WRAPPERS
// ================================
/**
 * Wrapper pour le chargement des sources (liste paginée)
 */
const {
    isFetching: isFetchingSources,
    fetch: fetchSources,
    error: errorSources,
    hasError: hasErrorSources,
} = useFetchWrapper(async (kwargs?: { reset?: boolean }) => {
    const reset = kwargs?.reset ?? false
    if (reset) {
        pagination.value.page = 1
        sources.value = []
    }
    if (!pagination.value.has_more && !reset) return
    const { data, has_more } = await services!.$getSources({
        page: pagination.value.page,
        limit: pagination.value.limit,
        building: true,
        ...getQueryParams.value,
    })
    if (reset) {
        sources.value = data
        receptionParSource.value = {}
    } else {
        sources.value.push(...data)
    }
    pagination.value.has_more = has_more
    // Enrichissement NON BLOQUANT : la page s'affiche, l'état de réception arrive
    // ensuite. Tant qu'il n'est pas là, l'icône reste neutre (aucun signal d'état).
    void fetchReception(data)
})

/**
 * État de RÉCEPTION des compteurs Enedis affichés, par `_id_source`
 * (`informations.pdl.data_retrieval.update.state`, servi par dapi depuis le verdict du
 * service enedis — DEV-1581, décision A11). `null` = dapi n'a pas su.
 *
 * ⚠️ Avant DEV-1581 l'icône wifi était VERTE dès que `monitoring.lastdata.date`
 * existait : mesuré le 02-03/09/2026, 154 546 compteurs au vert sur une donnée de plus
 * de 30 jours (DEV-1571). Une couleur est un jugement : elle vient du verdict ou elle
 * ne vient pas. C'est le MÊME bloc que celui qui porte la bannière — une seule source
 * de vérité pour l'icône et pour la phrase.
 */
const receptionParSource = ref<Record<string, string | null>>({})

/**
 * Charge l'état de réception d'une page de sources (PDL seulement — les autres types
 * gardent leur règle), en un appel dapi allégé (ni pricer ni variables). Un échec
 * laisse les icônes neutres : on ne fabrique pas un état.
 * @param items - Sources de la page qui vient d'arriver
 */
async function fetchReception(items: SourceListItem[]): Promise<void> {
    const ids = items
        .filter((item) => item.idtype === SOURCES_TYPES.PDL)
        .map((item) => item._id_source)
    if (ids.length === 0 || !services?.$getSourcesInformations) return
    try {
        const infos = await services.$getSourcesInformations(ids, undefined, {
            pricer: false,
            variable: false,
        })
        const etats: Record<string, string | null> = {}
        for (const info of infos ?? []) {
            etats[info._id_source] =
                info?.informations?.pdl?.data_retrieval?.update?.state ?? null
        }
        receptionParSource.value = { ...receptionParSource.value, ...etats }
    } catch {
        // dapi muet : aucun signal, la date nue reste seule à parler.
    }
}

/**
 * Wrapper pour le chargement des KPIs (total et indicateurs)
 */
const {
    isFetching: isFetchingKpis,
    fetch: fetchKpis,
    error: _errorKpis,
    hasError: _hasErrorKpis,
} = useFetchWrapper(
    async () => {
        const {
            total: totalCount,
            total_filter,
            total_consent_pending: consentPending,
            kpis: kpisData,
        } = await services!.$getSourcesCount(getQueryParams.value)
        total.value = totalCount
        totalFilter.value =
            total_filter ?? (filters.value.length > 0 ? totalCount : null)
        totalConsentPending.value = consentPending ?? 0
        kpis.value = kpisData || {}
    },
    (error) => {
        toast.add({
            severity: "error",
            summary: t("errorSummary"),
            detail: error?.message ?? t("loadKpisErrorMessage"),
            life: 3000,
        })
    }
)

/**
 * Wrapper pour l'export des sources
 */
const { isFetching: isFetchingExport, fetch: fetchExport } = useFetchWrapper(
    async () => {
        const url = await services!.$exportSources()
        if (url) {
            window.open(url, "_blank")
            toast.add({
                severity: "success",
                summary: t("exportSuccessSummary"),
                detail: t("exportSuccessDetail"),
                life: 3000,
            })
        } else {
            toast.add({
                severity: "warn",
                summary: t("exportNoFileSummary"),
                detail: t("exportNoFileDetail"),
                life: 3000,
            })
        }
    },
    () => {
        toast.add({
            severity: "error",
            summary: t("errorSummary"),
            detail: t("exportErrorDetail"),
            life: 3000,
        })
    }
)

// ================================
// COMPUTED
// ================================
/**
 * Nombre total de filtres sélectionnés
 */
const getTotalSelectedFilters = computed(() => filters.value.length)

/**
 * Convertit un SourceListItem (réponse API liste) en Source pour ItemSource et les handlers.
 * @param item - Item source tel que renvoyé par l’API liste
 * @returns Source au format attendu par ItemSource et les fonctions de la page
 */
function formatSourceListItemToSource(item: SourceListItem): Source {
    const rights = item.relations?.map((r) => r.rights) ?? []

    const isIot = IOT_TYPES_LIST?.includes(item.idtype)

    const type = (isIot ? "iot" : item.idtype) as SourceType

    const segment = item.segment ? item.segment.toUpperCase() : undefined

    let informations = {} as Record<string, unknown>

    if (type === "pdl") {
        informations = { pdl: { compteur: { segment } } }
    }
    if (type === "pce") {
        informations = {
            pce: {
                droit_access: {
                    etat: item.grdf_access?.etat_droit_acces,
                    debut_droit_acces: item.grdf_access?.date_debut_droit_acces,
                },
            },
        }
    }

    return {
        _id_source: item._id_source,
        idtype: item.idtype,
        idsource: item.idsource,
        name: item.name,
        type,
        address: {
            street: "",
            city: "",
            zipcode: "",
        },
        informations,
        created_at: "",
        rights,
        operator: item.operator,
        details:
            item.brand != null || item.model != null
                ? { brand: item.brand, model: item.model }
                : undefined,
        monitoring: {
            broadcaststatus: item.monitoring?.status?.broadcast ?? 0,
            lastdata: item?.monitoring?.lastdata,
        },
        channels: item.monitoring?.channels?.map((ch) => ({
            type: ch.type,
            name: ch.name ?? "",
        })),
        buildings: item.buildings?.map((b) => ({
            _id: b._id_building,
            name: b.name,
            niveau: "",
            address: {
                street: "",
                city: "",
                zipcode: "",
                inline_name: b.address,
            },
        })),
        consentRequestPending: Boolean(item.consentrequest_en_cours),
    }
}

/**
 * Liste des sources converties en Source pour affichage (ItemSource) et handlers.
 */
const getSourcesForDisplay = computed(() =>
    sources.value.map(formatSourceListItemToSource)
)

/**
 * Mappe les filtres UI vers les paramètres API
 */
const getQueryParams = computed(() => {
    return mapFiltersToApiParams(filters.value)
})

// Liste des sources : affichage en v-for avec chargement infini via IntersectionObserver
/** Référence de l’élément sentinelle en bas de liste pour déclencher le load more */
const loadMoreSentinel = ref<HTMLElement | null>(null)

/** Observer pour détecter quand la sentinelle entre dans le viewport (scroll Y page) */
let sentinelObserver: IntersectionObserver | null = null

/**
 * KPIs filtrés et formatés pour l'affichage (avec état actif pour le surlignage)
 */
const getFilteredKpis = computed(() => {
    const kpiList: Array<{
        key: string
        label: string
        value: number | string
        icon: string
        iconColor?: string
        active: boolean
    }> = []

    // Total (toujours affiché)
    kpiList.push({
        key: "total",
        label: t("total"),
        value: total.value ?? "-",
        icon: "pi pi-list",
        active: filters.value.length === 0,
    })

    const koKpi = kpis.value.find((kpi) => kpi.type === "ko")
    const batteryEOLKpi = kpis.value.find((kpi) => kpi.type === "batteryEOL")
    const battery0Kpi = kpis.value.find((kpi) => kpi.type === "battery0")
    const movedKpi = kpis.value.find((kpi) => kpi.type === "moved")
    // Perdus (inLoss)
    kpiList.push({
        key: "ko",
        label: t("perdus"),
        value: koKpi && koKpi?.value > 0 ? koKpi?.value : "-",
        icon: "fal fa-wifi-slash",
        iconColor: "var(--p-red-500)",
        active: filters.value.includes("ko"),
    })

    // Fin de vie (batteryEOL + battery0 : un seul KPI, filtre sur les deux)
    const batteryEOLCount =
        (batteryEOLKpi?.value ?? 0) + (battery0Kpi?.value ?? 0)
    const isFinDeVieActive =
        filters.value.includes("batteryEOL") &&
        filters.value.includes("battery0")
    kpiList.push({
        key: "batteryEOL",
        label: t("finDeVie"),
        value: batteryEOLCount > 0 ? batteryEOLCount : "-",
        icon: "fal fa-battery-slash",
        iconColor: "var(--p-red-500)",
        active: isFinDeVieActive,
    })

    // Capteurs déplacés (moved)
    kpiList.push({
        key: "moved",
        label: t("capteursDeplaces"),
        value: movedKpi && movedKpi?.value > 0 ? movedKpi?.value : "-",
        icon: "fal fa-location-dot-slash",
        iconColor: "var(--p-red-500)",
        active: filters.value.includes("moved"),
    })
    return kpiList
})

/**
 * Nombre de résultats à afficher (filtré si des filtres sont appliqués, sinon total)
 */
const getDisplayedCount = computed(() => {
    // Si des filtres sont appliqués, utiliser totalFilter s'il existe, sinon total
    if (filters.value.length > 0) {
        return totalFilter.value !== null ? totalFilter.value : total.value
    }
    // Sinon, afficher le total
    return total.value
})

/**
 * Les deux nombres servis par le bloc `plafond` (DEV-621, M5) : « X sur votre
 * compte, M facturées sur la période » ; `null` sans bloc servi (rien n'est
 * affiché, jamais un 0 à la place d'une absence)
 */
const getTexteDeuxNombres = computed(() =>
    texteDeuxNombres(companyPlafond.value)
)

/**
 * Affiche l’icône d’info sur le KPI Total (UC1 + abonnement actif)
 */
const getShowSourceCountBreakdownTooltip = computed(
    () => isClientHomeys.value && isSubscriptionActive.value
)

/**
 * Contenu du tooltip informatif à côté de la valeur du KPI Total
 */
const getSourceCountBreakdownTooltipContent = computed(() => {
    const line1 = t("sourceCountBreakdownLine1", { n: total.value })
    const line2 = t("sourceCountBreakdownLine2", {
        n: totalConsentPending.value,
    })
    return `${line1}<br/>${line2}`
})

// ================================
// SENTINELLE POUR LOAD MORE (IntersectionObserver sur scroll page)
// ================================
/**
 * Initialise l’IntersectionObserver sur la sentinelle pour charger la suite au scroll Y.
 */
function setupLoadMoreObserver(): void {
    if (!loadMoreSentinel.value) return
    sentinelObserver = new IntersectionObserver(
        (entries) => {
            const entry = entries[0]
            if (entry?.isIntersecting) {
                onFetchMore()
            }
        },
        { root: null, rootMargin: "200px", threshold: 0 }
    )
    sentinelObserver.observe(loadMoreSentinel.value)
}

/**
 * Désactive l’observer à la destruction du composant.
 */
function teardownLoadMoreObserver(): void {
    if (sentinelObserver) {
        if (loadMoreSentinel.value) {
            sentinelObserver.unobserve(loadMoreSentinel.value)
        }
        sentinelObserver.disconnect()
        sentinelObserver = null
    }
}

// ================================
// BUSINESS LOGIC
// ================================
/**
 * Charge la page suivante (load more)
 */
function onFetchMore(): void {
    if (pagination.value.has_more && !isFetchingSources.value) {
        pagination.value.page++
        fetchSources()
    }
}

/**
 * Indique si le bouton supprimer doit être affiché pour une source
 * @param source - Source à vérifier
 * @returns true si le bouton doit être affiché
 */
function shouldShowRemove(source: Source): boolean {
    // Exclure idtype: idmeteofrance, idopenmeteo, rte
    const excludedTypes = [
        SOURCES_TYPES.METEOFRANCE,
        SOURCES_TYPES.OPENMETEO,
        SOURCES_TYPES.RTE,
    ]
    const sourceType = source.idtype || source.type || ""
    if (excludedTypes.includes(sourceType)) {
        return false
    }
    // Permissions de suppression
    const isCompanyAdminAndHomeys = isCompanyAdmin.value && isClientHomeys.value
    const isOwnerNotHomeys =
        source.rights?.includes("owns") && !isClientHomeys.value

    return isCompanyAdminAndHomeys || isOwnerNotHomeys
}

function getLastMessageSensor(source: Source): string {
    if (!source?.monitoring?.lastdata?.date) {
        return "Aucun message reçu"
    }

    const formattedDate = formatDateFr(source.monitoring.lastdata.date, {
        dateStyle: "long",
        timeStyle: "short",
    })

    return `Dernier message reçu le ${formattedDate}`
}

// Configuration des statuts de diffusion standard
const wifiConfig: Record<
    number,
    { icon: string; color: string; tooltip?: string }
> = {
    2: { icon: "fal fa-wifi", color: "var(--p-green-500)" }, // Excellent - signal fort
    1: { icon: "fal fa-wifi-fair", color: "var(--p-orange-500)" }, // Moyen - attention
    0: { icon: "fal fa-wifi-slash", color: "var(--p-red-500)" }, // Mauvais - problème
}

/**
 * Récupère la configuration de l'état de la diffusion.
 * Pour les compteurs Enedis (PDL) : le VERDICT du service enedis via dapi (DEV-1581).
 * Pour les compteurs (METER) : on se base uniquement sur lastdata.date (pas de broadcastStatus).
 * Pour les autres types : on utilise broadcastStatus si présent, sinon lastdata.date pour "excellent".
 * @param source - Source
 * @returns Configuration de l'état de la diffusion
 */
function getWifiStatusConfig(source: Source) {
    // Compteur Enedis : l'icône suit le VERDICT (dapi `data_retrieval.update.state`),
    // jamais la présence de `lastdata.date` (DEV-1581, A11 ; défaut mesuré DEV-1571).
    // Inconnu ⇒ icône neutre, sans jugement — le texte « Dernier message reçu le … »
    // à côté reste inchangé et porte la date nue.
    if (source?.idtype === SOURCES_TYPES.PDL) {
        const reception = receptionWifiConfig(
            receptionParSource.value[source._id_source]
        )
        return {
            icon: reception.icon,
            color: reception.color,
            tooltip: t(reception.tooltipKey),
        }
    }
    const isMeter = METER_TYPES_LIST.includes(source?.idtype)
    if (isMeter) {
        return source?.monitoring?.lastdata?.date
            ? wifiConfig[2]
            : wifiConfig[0]
    }
    const broadcastStatus = source.monitoring?.broadcaststatus
    if (broadcastStatus == null) {
        if (source?.monitoring?.lastdata?.date) return wifiConfig[2]
        return wifiConfig[0]
    }
    return wifiConfig?.[broadcastStatus as keyof typeof wifiConfig]
}

/**
 * Récupère le label de l'état du droit d'accès
 * @param etat - État du droit d'accès
 * @returns Label de l'état du droit d'accès
 */
function getDroitAccesEtatConfig(etat: DroitAccesEtat): {
    value: string
    severity: "info" | "success" | "danger"
    tooltip?: string
} {
    if (etat === "A valider") {
        return { value: t("dda.a_valider"), severity: "info" }
    } else if (etat === "Active") {
        return { value: t("dda.active"), severity: "success" }
    }
    return {
        value: t("dda.error"),
        severity: "danger",
        tooltip: t("dda.error_tooltip"),
    }
}

// ================================
// INTERACTIONS
// ================================
/**
 * Gère le clic sur le bouton paramètres
 * @param source - Source sélectionnée
 */
function onClickSettings(source: Source): void {
    const sourceId = source._id_source
    if (sourceId) {
        redirectWrapper("/source/:_id_source/info-general", {
            _id_source: sourceId,
        })
    }
}

/**
 * Gère le clic sur le bouton données
 * @param source - Source sélectionnée
 */
function onClickData(source: Source): void {
    const sourceId = source._id_source
    if (sourceId) {
        redirectWrapper("/source/:_id_source/data", { _id_source: sourceId })
    }
}

/**
 * Navigation bâtiment pour une source « demande de consentement en cours » (aligné sur ItemSource)
 */
function onNavigateConsentRequestToBuilding(source: Source): void {
    const raw = source.buildings ?? []
    const list = raw.map((b) => ({
        _id: b._id,
        name: b.name || t("consentBuildingListUnnamed"),
        address: b.address,
        niveau: b.niveau ?? "",
    }))
    if (list.length === 0) {
        const sourceId = source._id_source
        if (sourceId) {
            redirectWrapper("/source/:_id_source/info-general", {
                _id_source: sourceId,
            })
        }
        return
    }
    if (list.length === 1) {
        redirectWrapper("/building/:_id_building/compteur", {
            _id_building: list[0]!._id,
        })
        return
    }
    consentBuildingListForDialog.value = list
    consentBuildingListDialogVisible.value = true
}

/**
 * Gère le clic sur la carte source (item entier) : données, ou bâtiment si demande de consentement.
 * Ne redirige pas si le clic provient de la zone d’actions ou d’un bouton.
 * @param event - Événement de clic ou de clavier
 * @param source - Source concernée
 */
function onClickSourceCard(
    event: MouseEvent | KeyboardEvent,
    source: Source
): void {
    const target = event.target as HTMLElement
    if (target.closest(".item-source__actions") || target.closest("button")) {
        return
    }
    if (source.consentRequestPending) {
        onNavigateConsentRequestToBuilding(source)
        return
    }
    onClickData(source)
}

/**
 * Gère le clic sur le bouton supprimer : ouvre la boîte de suppression
 * @param source - Source à supprimer
 */
function onClickDelete(source: Source): void {
    sourceToDelete.value = source
    deleteDialogVisible.value = true
}

/**
 * Après une suppression réussie (toast géré par SourceDeleteDialog) : retire
 * la ligne, relit les indicateurs et le plafond servi (DEV-621, M4)
 * @param payload - source supprimée
 */
async function onSourceDeleted({
    sourceId,
}: {
    sourceId: string
}): Promise<void> {
    sourceToDelete.value = null
    const index = sources.value.findIndex((s) => s._id_source === sourceId)
    if (index !== -1) {
        sources.value.splice(index, 1)
        total.value--
    }
    await fetchKpis()
    await rafraichirPlafondServi()
}

/**
 * Gère le clic sur un KPI pour toggler le filtre correspondant
 * "Fin de vie" (batteryEOL) toggle les deux filtres batteryEOL et battery0 pour rester aligné avec le comptage.
 * @param kpi - Clé du KPI
 */
function onClickKpi(kpi: string): void {
    if (kpi === "total") {
        filters.value = []
    } else if (kpi === "batteryEOL") {
        // Fin de vie = batteryEOL + battery0 : on toggle les deux ensemble
        const bothActive =
            filters.value.includes("batteryEOL") &&
            filters.value.includes("battery0")
        if (bothActive) {
            filters.value = filters.value.filter(
                (f) => f !== "batteryEOL" && f !== "battery0"
            )
        } else {
            const next = [...filters.value]
            if (!next.includes("batteryEOL")) next.push("batteryEOL")
            if (!next.includes("battery0")) next.push("battery0")
            filters.value = next
        }
    } else {
        if (filters.value.includes(kpi)) {
            filters.value = filters.value.filter((f) => f !== kpi)
        } else {
            filters.value = [...filters.value, kpi]
        }
    }
}

/**
 * Gère le clic sur le bouton de filtre : synchronise les sélections en attente et ouvre le popover
 * @param event - Événement de clic
 */
function onClickFilterButton(event: Event): void {
    pendingFilters.value = [...filters.value]
    filterPopoverRef.value?.show(event)
}

/**
 * Met à jour la sélection en attente pour un filtre (checkbox)
 * @param filterValue - Valeur du filtre
 * @param checked - État coché / décoché
 */
function onPendingFilterChange(filterValue: string, checked: boolean): void {
    if (checked) {
        if (!pendingFilters.value.includes(filterValue)) {
            pendingFilters.value = [...pendingFilters.value, filterValue]
        }
    } else {
        pendingFilters.value = pendingFilters.value.filter(
            (f) => f !== filterValue
        )
    }
}

/**
 * Applique les filtres en attente, lance la recherche et ferme le popover
 */
function onApplyFilterSearch(): void {
    const nextFilters = [...pendingFilters.value]
    const apiBefore = mapFiltersToApiParams(filters.value)
    const apiAfter = mapFiltersToApiParams(nextFilters)
    const paramsChanged = JSON.stringify(apiBefore) !== JSON.stringify(apiAfter)

    if (paramsChanged) {
        skipNextDebouncedQueryParamsFetch.value = true
    }
    filters.value = nextFilters
    filterPopoverRef.value?.hide()
    if (paramsChanged) {
        fetchSources({ reset: true })
        fetchKpis()
    }
}

/**
 * Réinitialise tous les filtres (applique et ferme le popover)
 */
function onResetFilters(): void {
    filters.value = []
    filterPopoverRef.value?.hide()
}

/**
 * Déclenche l'export des sources
 */
function onExport(): void {
    fetchExport()
}

/**
 * Relance le chargement de la liste et des totaux / KPIs après une erreur
 */
function onRetryLoadSources(): void {
    fetchSources({ reset: true })
    fetchKpis()
}

// ================================
// WATCHERS
// ================================
/**
 * Watch debounced sur les filtres pour refetch automatique
 */
/**
 * Synchronise les filtres avec la query string (exécuté avant watchDebounced pour éviter double fetch au montage)
 */
watch(
    () => route.query.filters,
    (queryFilters) => {
        if (queryFilters) {
            filters.value = Array.isArray(queryFilters)
                ? (queryFilters as string[])
                : (queryFilters as string)
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean)
        } else {
            filters.value = []
        }
    },
    { immediate: true }
)

/**
 * Retire le filtre `eau` si l'entreprise n'a pas la bêta-feature (après chargement du contexte).
 */
watch(
    [getHasSigduBeta, companyId],
    () => {
        if (!companyId.value) return
        if (!getHasSigduBeta.value) {
            if (filters.value.includes("eau")) {
                filters.value = filters.value.filter((f) => f !== "eau")
            }
            if (pendingFilters.value.includes("eau")) {
                pendingFilters.value = pendingFilters.value.filter(
                    (f) => f !== "eau"
                )
            }
        }
    },
    { immediate: true }
)

/**
 * Déclenche fetch sources + KPIs quand les paramètres de requête changent (debounced).
 * Premier chargement : exécution immédiate dans onMounted ; ensuite debounced.
 * Clic sur « Rechercher » : fetch immédiat dans onApplyFilterSearch (sans attendre le debounce).
 */
watchDebounced(
    getQueryParams,
    () => {
        if (skipNextDebouncedQueryParamsFetch.value) {
            skipNextDebouncedQueryParamsFetch.value = false
            return
        }
        fetchSources({ reset: true })
        fetchKpis()
    },
    { debounce: 1000, maxWait: 5000 }
)

/**
 * Synchronise la query string avec les filtres
 */
watch(filters, (newFilters) => {
    const query = {
        ...route.query,
        filters: newFilters.length > 0 ? newFilters : undefined,
    }
    router.replace({
        path: route.path,
        query,
    })
})

// ================================
// LIFECYCLE
// ================================
onBeforeMount(async () => {
    // Charge les opérateurs et les modèles de sources pour l'affichage dans ItemSource
    await Promise.all([
        configStore.fetchOperators(),
        configStore.fetchSourceModels(),
    ])
})
/**
 * Initialise la page au montage
 */
onMounted(() => {
    fetchSources({ reset: true })
    fetchKpis()
})

/**
 * Configure l’observer lorsque la sentinelle est rendue (après chargement des sources).
 */
watch(loadMoreSentinel, (el) => {
    teardownLoadMoreObserver()
    if (el) {
        setupLoadMoreObserver()
    }
})

onUnmounted(teardownLoadMoreObserver)
</script>

<template>
    <div class="page page-source-list flex min-h-0 flex-1 flex-col gap-6 p-6">
        <!-- ================================ -->
        <!-- EN-TÊTE : BREADCRUMB, TITRE, EXPORT -->
        <!-- ================================ -->
        <HeaderPage
            :title="t('title')"
            :breadcrumb="getBreadcrumb"
            class-header="flex items-center justify-between gap-4"
        >
        </HeaderPage>

        <!-- ================================ -->
        <!-- KPIs -->
        <!-- ================================ -->
        <div
            v-if="isFetchingKpis && total === 0"
            class="grid grid-cols-2 md:grid-cols-4 justify-center items-center gap-4 w-max mx-auto"
        >
            <BaseSkeleton
                v-for="i in 4"
                :key="`skeleton-kpi-${i}`"
                height="5rem"
                :width="isMobile ? '10.5rem' : '12rem'"
                class="rounded-lg w-full md:max-w-48 max-w-[10.5rem]"
            />
        </div>

        <div
            v-else-if="!isFetchingKpis || Object.keys(kpis).length > 0"
            class="grid grid-cols-2 md:grid-cols-4 justify-center items-center gap-4 w-max mx-auto"
        >
            <BaseKPI
                v-for="kpi in getFilteredKpis"
                :key="kpi.key"
                :label="kpi.label"
                :value="kpi.value"
                :type="kpi.key"
                :icon="kpi.icon"
                :icon-color="kpi.iconColor"
                :highlight="kpi.active"
                @click="onClickKpi"
                :pt="{ root: 'min-w-[12rem]' }"
            >
                <template
                    v-if="
                        kpi.key === 'total' &&
                        getShowSourceCountBreakdownTooltip
                    "
                    #value
                >
                    <div
                        class="inline-flex min-w-0 max-w-full flex-nowrap items-center justify-center gap-1"
                    >
                        <BaseIcon
                            v-if="kpi.icon"
                            class="shrink-0"
                            :icon="kpi.icon"
                            size="1rem"
                            :color="kpi.iconColor ?? 'var(--p-primary-600)'"
                        />
                        <span
                            v-if="kpi.value !== undefined && kpi.value !== null"
                            class="shrink-0"
                        >
                            {{ kpi.value }}
                        </span>
                        <span class="inline-flex" @click.stop>
                            <BaseTooltipMessage
                                :title="t('sourceCountBreakdownTitle')"
                                :content="getSourceCountBreakdownTooltipContent"
                                position="top"
                                severity="info"
                                size="small"
                            />
                        </span>
                    </div>
                </template>
            </BaseKPI>
        </div>
        <p
            v-if="getTexteDeuxNombres"
            class="page-source-list__facturees m-0 text-center text-sm text-primary"
        >
            {{ getTexteDeuxNombres }}
        </p>

        <!-- ================================ -->
        <!-- Erreur chargement sources -->
        <!-- ================================ -->
        <BaseAppMessage v-if="hasErrorSources" severity="error" class="mb-4">
            <span>{{
                errorSources?.message ?? t("loadSourcesErrorMessage")
            }}</span>
            <BaseButton
                class="mt-2"
                size="small"
                :label="t('retry')"
                @click="onRetryLoadSources"
            />
        </BaseAppMessage>

        <!-- ================================ -->
        <!-- RÉSULTATS -->
        <!-- ================================ -->
        <div
            class="page-source-list__results flex items-center justify-between gap-4 flex-wrap"
        >
            <p class="text-sm text-gray-600">
                {{ t("resultsFound") }}
                <span class="font-semibold text-gray-900">
                    {{ getDisplayedCount }}
                </span>
            </p>
            <div class="flex items-center gap-4 flex-wrap">
                <BaseButton
                    :label="t('export')"
                    icon="pi pi-download"
                    @click="onExport"
                    size="small"
                    :disabled="isFetchingExport || isFetchingSources"
                    :loading="isFetchingExport"
                />
                <BaseButton
                    size="small"
                    :label="
                        getTotalSelectedFilters > 0
                            ? t('filtersWithCount', {
                                  count: getTotalSelectedFilters,
                              })
                            : t('filters')
                    "
                    icon="pi pi-filter"
                    :disabled="isFetchingExport || isFetchingSources"
                    severity="secondary"
                    @click="onClickFilterButton"
                />
            </div>
        </div>

        <!-- ================================ -->
        <!-- LISTE DES SOURCES (v-for + IntersectionObserver) -->
        <!-- ================================ -->
        <div v-if="sources && (sources.length > 0 || isFetchingSources)">
            <!-- Skeleton liste initiale (10 cartes) -->
            <div
                v-if="isFetchingSources && sources.length === 0"
                class="flex flex-col gap-6"
            >
                <BaseSkeleton
                    v-for="i in 10"
                    :key="`skeleton-initial-${i}`"
                    width="100%"
                    height="5rem"
                    class="rounded-lg"
                />
            </div>
            <template v-else>
                <div
                    v-for="source in getSourcesForDisplay"
                    :key="source._id_source"
                    class="page-source-list__item-wrapper my-6 cursor-pointer"
                    role="button"
                    tabindex="0"
                    @click="onClickSourceCard($event, source)"
                    @keydown.enter="onClickSourceCard($event, source)"
                    @keydown.space.prevent="onClickSourceCard($event, source)"
                >
                    <ItemSource
                        class="page-source-list__item"
                        :source="source"
                        :show-remove="
                            shouldShowRemove(source) &&
                            !source.consentRequestPending
                        "
                        :show-toggle-used="false"
                        :disable-brand-image="
                            METER_TYPES_LIST.includes(source.idtype)
                        "
                        @click:settings="onClickSettings(source)"
                        @click:data="onClickData(source)"
                        @click:delete="onClickDelete(source)"
                    >
                        <template #info="{ type }">
                            <!-- SEGMENT du compteur -->
                            <div class="flex items-center gap-2 flex-wrap mt-2">
                                <BaseTag
                                    v-if="
                                        METER_TYPES_LIST.includes(
                                            source.idtype
                                        ) && !source?.idtype?.startsWith('snc-')
                                    "
                                    :value="source.idtype?.toUpperCase()"
                                    severity="primary"
                                />
                                <!-- SEGMENT du compteur -->
                                <BaseTag
                                    v-if="
                                        type === 'pdl' &&
                                        source.informations?.pdl?.compteur
                                            ?.segment
                                    "
                                    :value="
                                        source.informations?.pdl?.compteur
                                            ?.segment
                                    "
                                />
                                <!-- État du droit d'accès GRDF -->
                                <BaseTag
                                    v-if="type === 'pce'"
                                    :value="
                                        getDroitAccesEtatConfig(
                                            source.informations?.pce
                                                ?.droit_access?.etat ?? 'Erreur'
                                        )?.value
                                    "
                                    :severity="
                                        getDroitAccesEtatConfig(
                                            source.informations?.pce
                                                ?.droit_access?.etat ?? 'Erreur'
                                        )?.severity
                                    "
                                    v-tooltip.top="
                                        getDroitAccesEtatConfig(
                                            source.informations?.pce
                                                ?.droit_access?.etat ?? 'Erreur'
                                        )?.tooltip
                                    "
                                />
                            </div>
                            <div
                                class="text-sm text-gray-500 flex items-center gap-2 flex-wrap mt-2"
                            >
                                <BaseIcon
                                    v-if="getWifiStatusConfig(source)"
                                    v-tooltip.top="
                                        getWifiStatusConfig(source)?.tooltip
                                    "
                                    class="w-fit"
                                    :icon="getWifiStatusConfig(source)?.icon"
                                    :style="{
                                        color: getWifiStatusConfig(source)
                                            ?.color,
                                    }"
                                />
                                {{ getLastMessageSensor(source) }}
                            </div>
                        </template>
                    </ItemSource>
                </div>
                <!-- Sentinelle pour charger la suite au scroll Y -->
                <div
                    v-if="pagination.has_more"
                    ref="loadMoreSentinel"
                    class="page-source-list__sentinel flex items-center justify-center"
                    aria-hidden="true"
                />
                <!-- Skeleton chargement pages suivantes (4 cartes) -->
                <div
                    v-if="isFetchingSources && sources.length > 0"
                    class="flex flex-col gap-6 mt-2"
                >
                    <BaseSkeleton
                        v-for="i in 4"
                        :key="`skeleton-more-${i}`"
                        width="100%"
                        height="5rem"
                        class="rounded-lg"
                    />
                </div>
            </template>
        </div>

        <!-- ================================ -->
        <!-- EMPTY STATE -->
        <!-- ================================ -->
        <div
            v-if="
                sources &&
                sources.length === 0 &&
                !isFetchingSources &&
                !hasErrorSources
            "
            class="page-source-list__empty flex flex-col items-center justify-center p-12 text-center"
        >
            <i class="pi pi-inbox text-4xl text-gray-400 mb-4"></i>
            <p class="text-gray-600">{{ t("emptyState") }}</p>
        </div>

        <!-- ================================ -->
        <!-- MENU DE FILTRES (Popover avec checkboxes + Rechercher) -->
        <!-- ================================ -->
        <BasePopover
            ref="filterPopoverRef"
            class="page-source-list__filter-popover"
        >
            <div
                class="flex flex-col gap-3 min-w-[280px] max-w-[280px] max-h-[20rem] overflow-y-auto"
            >
                <template
                    v-for="category in getFilterCategories"
                    :key="category.name"
                >
                    <div class="flex flex-col gap-1.5">
                        <span class="text-sm font-semibold text-gray-700">{{
                            category.name
                        }}</span>
                        <div class="flex flex-col gap-1">
                            <label
                                v-for="filter in category.filters"
                                :key="filter.value"
                                class="flex items-center gap-2 cursor-pointer rounded p-1.5 hover:bg-gray-100"
                            >
                                <BaseCheckbox
                                    :model-value="
                                        pendingFilters.includes(filter.value)
                                    "
                                    binary
                                    :input-id="`filter-${filter.value}`"
                                    @update:model-value="
                                        (v: boolean) =>
                                            onPendingFilterChange(
                                                filter.value,
                                                v
                                            )
                                    "
                                />
                                <span class="text-sm">{{ filter.label }}</span>
                            </label>
                        </div>
                    </div>
                </template>
            </div>
            <div
                class="flex items-center justify-between gap-2 pt-3 mt-2 border-t border-gray-200"
            >
                <BaseButton
                    :label="t('filterReset')"
                    text
                    size="small"
                    icon="pi pi-refresh"
                    :disabled="pendingFilters.length === 0"
                    @click="onResetFilters"
                />
                <BaseButton
                    :label="t('search')"
                    icon="pi pi-search"
                    size="small"
                    @click="onApplyFilterSearch"
                />
            </div>
        </BasePopover>

        <!-- ================================ -->
        <!-- DIALOG DE CONFIRMATION -->
        <!-- ================================ -->
        <SourceDeleteDialog
            v-if="sourceToDelete?._id_source"
            v-model:visible="deleteDialogVisible"
            :source-id="sourceToDelete._id_source"
            :source-label="sourceToDelete.idsource || sourceToDelete._id_source"
            @success="onSourceDeleted"
        />

        <DialogMeterBuildingList
            v-if="consentBuildingListForDialog.length > 1"
            v-model:visible="consentBuildingListDialogVisible"
            :buildings="consentBuildingListForDialog"
            target="compteur"
        />
    </div>
</template>
